import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  FormHelperText,
  Grid,
  OutlinedInput,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { calculatePrice, clearCalculatedPrice } from '../../redux/slices/planSlice';

const PriceCalculator = ({ plan, onClose }) => {
  const dispatch = useDispatch();
  const { calculatedPrice, loading, error } = useSelector((state) => state.plans);
  
  const [dealersCount, setDealersCount] = useState(plan?.dealerLimit || 0);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedAddOns, setSelectedAddOns] = useState([]);

  useEffect(() => {
    return () => {
      dispatch(clearCalculatedPrice());
    };
  }, [dispatch]);

  const handleCalculate = () => {
    dispatch(calculatePrice({
      planId: plan._id,
      dealersCount: Number(dealersCount),
      selectedAddOns: selectedAddOns.map(title => ({ title })),
      billingCycle,
    }));
  };

  const handleBillingCycleChange = (event, newCycle) => {
    if (newCycle !== null) {
      setBillingCycle(newCycle);
    }
  };

  const getBasePrice = () => {
    return billingCycle === 'yearly' 
      ? plan.billingOptions?.yearly?.price 
      : plan.billingOptions?.monthly?.price;
  };

  const getDiscount = () => {
    return billingCycle === 'yearly'
      ? plan.billingOptions?.yearly?.discount
      : plan.billingOptions?.monthly?.discount;
  };

  const extraDealers = Math.max(0, dealersCount - plan.dealerLimit);
  const extraDealerCost = extraDealers * plan.extraDealerPrice;

  // Calculate yearly savings
  const monthlyPrice = plan.billingOptions?.monthly?.price;
  const yearlyTotal = monthlyPrice * 12;
  const yearlySavings = yearlyTotal - (plan.billingOptions?.yearly?.price || 0);

  return (
    <Box sx={{ mt: 2 }}>
      <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', mb: 2 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Plan: <strong>{plan.name}</strong>
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            color="primary"
            value={billingCycle}
            exclusive
            onChange={handleBillingCycleChange}
            fullWidth
          >
            <ToggleButton value="monthly">Monthly</ToggleButton>
            <ToggleButton value="yearly">
              Yearly {yearlySavings > 0 && `(Save $${yearlySavings})`}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Base Price: <strong>${getBasePrice()}</strong>/{billingCycle === 'yearly' ? 'year' : 'month'}
        </Typography>
        
        {getDiscount() > 0 && (
          <Typography variant="body2" color="success.main" gutterBottom>
            Discount: {getDiscount()}% off
          </Typography>
        )}

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Included Dealers: <strong>{plan.dealerLimit}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Extra Dealer Price: <strong>${plan.extraDealerPrice}</strong>/{billingCycle === 'yearly' ? 'year' : 'month'}
        </Typography>
        {plan.setupFee > 0 && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Setup Fee: <strong>${plan.setupFee}</strong> (one-time)
          </Typography>
        )}
      </Paper>

      <TextField
        fullWidth
        label="Number of Dealers"
        type="number"
        value={dealersCount}
        onChange={(e) => setDealersCount(Math.max(0, parseInt(e.target.value) || 0))}
        InputProps={{ inputProps: { min: 0 } }}
        sx={{ mb: 2 }}
      />

      {extraDealers > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Extra dealers: {extraDealers} × ${plan.extraDealerPrice} = ${extraDealerCost} 
          ({billingCycle === 'yearly' ? 'per year' : 'per month'})
        </Alert>
      )}

      {plan?.addOns?.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Select Add-ons:
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="addons-select-label">Add-ons</InputLabel>
            <Select
              labelId="addons-select-label"
              id="addons-select"
              multiple
              value={selectedAddOns}
              onChange={(e) => setSelectedAddOns(e.target.value)}
              input={<OutlinedInput label="Add-ons" />} // Fixed: Using OutlinedInput instead of TextField
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {plan.addOns.map((addon) => (
                <MenuItem key={addon.title} value={addon.title}>
                  <ListItemText 
                    primary={addon.title} 
                    secondary={`$${addon.price} (${addon.billingType})`}
                  />
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Add-ons may be one-time, monthly, or yearly
            </FormHelperText>
          </FormControl>
        </>
      )}

      <Button
        fullWidth
        variant="contained"
        onClick={handleCalculate}
        disabled={loading}
        sx={{ mb: 2 }}
      >
        {loading ? <CircularProgress size={24} /> : 'Calculate Total'}
      </Button>

      {calculatedPrice && (
        <Paper elevation={3} sx={{ p: 3, bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="h6" gutterBottom align="center">
            Total {calculatedPrice.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} Price
          </Typography>
          <Typography variant="h3" align="center" fontWeight="bold">
            ${calculatedPrice.totalAmount}
          </Typography>
          
          <Divider sx={{ my: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />
          
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="body2">Base Price:</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" align="right">${calculatedPrice.basePrice}</Typography>
            </Grid>
            
            {calculatedPrice.extraDealers > 0 && (
              <>
                <Grid item xs={6}>
                  <Typography variant="body2">Extra Dealers:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" align="right">
                    +${calculatedPrice.extraDealerCost}
                  </Typography>
                </Grid>
              </>
            )}
            
            {calculatedPrice.setupFee > 0 && (
              <>
                <Grid item xs={6}>
                  <Typography variant="body2">Setup Fee:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" align="right">
                    +${calculatedPrice.setupFee}
                  </Typography>
                </Grid>
              </>
            )}
            
            {calculatedPrice.discount > 0 && (
              <>
                <Grid item xs={6}>
                  <Typography variant="body2">Discount ({calculatedPrice.discount}%):</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" align="right" color="success.light">
                    -${(calculatedPrice.totalAmount * calculatedPrice.discount / 100).toFixed(2)}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
          
          {calculatedPrice.savings > 0 && (
            <Alert severity="success" sx={{ mt: 2 }}>
              You save ${calculatedPrice.savings} with yearly billing!
            </Alert>
          )}
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </Box>
    </Box>
  );
};

export default PriceCalculator;